import { Injectable } from '@angular/core'
import { ReportClaims, UserClaims } from '../../../../src/commons/enums/user-claims.enum'
import { ApiService } from '../api/api.service'
import { CacheService } from './cache.service'
import { UserApiService } from '../../../../src/security/models/services/users/userApi.service'
import { UserPermissionsDto } from '../../../../src/security/models/user-permissions.model'

@Injectable()
export class ClaimsService {
  constructor(private cacheService: CacheService, private userApiService: UserApiService, private apiService: ApiService) { }

  public async isLoggedUserAdmin(): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    //Admin user is anyone who has the "edit user" or "create new user" claim permissions
    return (
      loggedUserClaims.findIndex((q) => q.key == UserClaims.InviteNewUser) >= 0 ||
      loggedUserClaims.findIndex((q) => q.key == UserClaims.EditUser) >= 0
    )
  }

  public async isUserAdmin(userId: number, permissions: UserPermissionsDto = null): Promise<boolean> {
    //Admin user is anyone who has the "edit user" or "create new user" claim permissions

    let userPermissions: UserPermissionsDto = permissions
    if (permissions == null)
      userPermissions = await this.userApiService.getUserPermissions(userId)

    return (
      userPermissions?.claims.filter(
        (c) => c.key == UserClaims.InviteNewUser || c.key == UserClaims.EditUser
      ).length > 0
    )
  }

  public async hasClaim(claim: UserClaims): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    return loggedUserClaims.findIndex((q) => q.key == claim) >= 0
  }

  public async hasAnyDashboardKPIClaim(): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    return loggedUserClaims.filter((q) => q.key.endsWith(UserClaims.DashboardKPI)).length >= 1
  }

  public async hasSomeReportClaim(): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    return loggedUserClaims.some((q) => ReportClaims.some((w) => w == q.key))
  }

  public async canDownloadReportFiles(): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    const downloadReportClaims = await this.apiService.getUserReportDownloads()
    return loggedUserClaims.some((q) => downloadReportClaims.some((w) => w.claimKey == q.key))
  }

  public async canUpdateCustomerCardPackage(): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    return loggedUserClaims.filter((q) => q.key === UserClaims.UpdateCardPackage).length >= 1
  }

  public async canLoggedUserChangeUserStatus(): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    return loggedUserClaims.findIndex((q) => q.key == UserClaims.ChangeUserStatus) >= 0
  }

  public async canLoggedUserEditUser(): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    return loggedUserClaims.findIndex((q) => q.key == UserClaims.EditUser) >= 0
  }

  public async canLoggedUserEditInternalUser(): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    return loggedUserClaims.findIndex((q) => q.key == UserClaims.EditInternalUser) >= 0
  }

  public async canLoggedUserResetPasswords(): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    return loggedUserClaims.findIndex((q) => q.key == UserClaims.ResetPassword) >= 0
  }

  public async canLoggedUserViewTransactions(): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    return loggedUserClaims.findIndex((q) => q.key == UserClaims.TransactionSearch) >= 0
  }

  public async canLoggedUserViewFundsRecovery(): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    return loggedUserClaims.findIndex((q) => q.key == UserClaims.FundsRecoveryAdmin || q.key == UserClaims.FundsRecoveryViewer) >= 0
  }

  public async canLoggedUserFundsRecoveryAdmin(): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    return loggedUserClaims.findIndex((q) => q.key == UserClaims.FundsRecoveryAdmin) >= 0
  }

  public async canLoggedUserViewNegativeBalanceSummary(): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    return loggedUserClaims.findIndex((q) => q.key == UserClaims.NegativeBalanceSummaryViewer) >= 0
  }
  public async canLoggedUserEditCardArt(): Promise<boolean> {
    const loggedUserClaims = await this.cacheService.getUserClaims()
    return loggedUserClaims.findIndex((q) => q.key == UserClaims.UpdateCardArt) >= 0
  }
}
